import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  Skeleton,
  Input,
  Select,
  Table,
  Pagination,
} from "antd";
import { debounce } from "lodash";
import { useHttp } from "../../../utils/DataHendler";
import { useNavigate } from "react-router-dom";
import { CreditCardFilled } from "@ant-design/icons";
import { useMediaQuery } from "react-responsive";

export const GridCard = () => {
  const { Search } = Input;
  const { getData } = useHttp();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryParams, setQueryParams] = useState({});
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // Visitor type lookup state
  const [visitorTypeOptions, setVisitorTypeOptions] = useState([]);

  // Fetch card data with pagination (fix for nested data structure)
  const getCard = async (params = {}) => {
    try {
      setLoading(true);
      const response = await getData(
        "/card",
        {
          params: {
            ...params,
            page: currentPage,
            limit: pageSize,
          },
        },
        true
      );
      // Use correct structure: response.data.data (object), response.data.data.data (array), response.data.data.pagination (object)
      const cardData = response.data?.data || [];
      const pagination = response.data?.pagination || {};
      setData(cardData);
      console.log(response.data?.data);
      setTotal(pagination.total || 0);
      setCurrentPage(pagination.page || 1);
      setPageSize(pagination.limit || 10);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching card data:", error);
      setData([]);
      setTotal(0);
      setLoading(false);
    }
  };

  // Fetch visitor type lookup data
  const fetchVisitorTypeLookup = async () => {
    try {
      const response = await getData("/visitor-type/lookup?limit=20", {}, true);
      const types = response.data.data || [];
      setVisitorTypeOptions(
        types.map((type) => ({
          value: type.id,
          label: type.name,
        }))
      );
    } catch (error) {
      console.error("Error fetching visitor type lookup:", error);
      setVisitorTypeOptions([]);
    }
  };

  // Debounced filter for search/type
  const debouncedFilter = useCallback(
    debounce((filterType, value) => {
      let newParams = { ...queryParams };
      if (filterType === "search") {
        const trimmedValue = value.trim();
        if (trimmedValue) {
          newParams.search = trimmedValue;
        } else {
          delete newParams.search;
        }
      } else if (filterType === "type") {
        if (value) {
          newParams.visitor_type_id = value;
        } else {
          delete newParams.visitor_type_id;
        }
      }
      setCurrentPage(1);
      setQueryParams(newParams);
    }, 500),
    [queryParams]
  );

  // Search input handler
  const handleSearchChange = (e) => {
    debouncedFilter("search", e.target.value);
  };

  // Type dropdown handler
  const handleTypeChange = (value) => {
    debouncedFilter("type", value);
  };

  // Table pagination handler (mobile)
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Desktop pagination handler
  const handleDesktopPagination = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  // Fetch data on filter/page/size change
  useEffect(() => {
    getCard(queryParams);
    // eslint-disable-next-line
  }, [queryParams, currentPage, pageSize]);

  // Fetch visitor type lookup on mount
  useEffect(() => {
    fetchVisitorTypeLookup();
    // eslint-disable-next-line
  }, []);

  // Kolom untuk tabel
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (text, record) => (
        <span
          style={{
            fontSize: isMobile ? "14px" : "16px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => (
        <span
          style={{
            fontSize: isMobile ? "12px" : "14px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_take",
      key: "is_take",
      render: (isTake) => (
        <Badge
          dot={true}
          style={{
            backgroundColor: isTake ? "#41B3A2" : "#F7374F",
            padding: "8px",
            boxShadow: `0 0 0 1px ${isTake ? "#15B392" : "#A62C2C"}`,
            borderRadius: "50%",
          }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: isMobile ? "10px" : "20px" }}>
      {/* Search Input */}
      <Search
        style={{
          marginRight: isMobile ? "0px" : "5px",
          width: isMobile ? "100%" : "50%",
          marginBottom: "20px",
        }}
        placeholder="Search name"
        allowClear
        onChange={handleSearchChange}
        enterButton
      />

      {/* Dropdown Filter untuk Type Card */}
      <Select
        placeholder="Select Visitor Type"
        allowClear
        menuItemSelectedIcon={<CreditCardFilled />}
        style={{
          width: isMobile ? "100%" : "30%",
          marginBottom: "20px",
        }}
        onChange={handleTypeChange}
        options={visitorTypeOptions}
      />

      {/* Tampilkan Skeleton jika sedang loading */}
      {loading ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : isMobile ? (
        // Tabel untuk tampilan mobile dengan pagination
        <Table
          dataSource={data}
          columns={columns}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          scroll={{ x: true }}
          onRow={(record) => ({
            onClick: () => navigate(`/user/detail/card/${record.code}`),
          })}
          style={{ marginTop: "20px" }}
        />
      ) : (
        // Grid untuk tampilan desktop dengan pagination
        <>
          <Row gutter={[16, 16]} justify="center">
            {data.map((item) => (
              <Col
                key={item.id}
                xs={24}
                sm={12}
                md={24 / 5}
                lg={24 / 5}
                xl={4}
                style={{ display: "flex" }}
              >
                <Badge
                  dot={true}
                  style={{
                    backgroundColor: item.is_take ? "#41B3A2" : "#F7374F",
                    padding: "8px",
                    boxShadow: `0 0 0 1px ${
                      item.is_take ? "#15B392" : "#A62C2C"
                    }`,
                    borderRadius: "50%",
                    position: "absolute",
                    top: 10,
                    right: 10,
                  }}
                >
                  <Card
                    hoverable
                    onClick={() => {
                      navigate(`/user/detail/card/${item.code}`);
                    }}
                    style={{
                      width: "100%",
                      height: 150,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        padding: "10px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                      }}
                    >
                      <h3
                        style={{
                          margin: "0 0 10px 0",
                          fontSize: "16px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.code}
                      </h3>
                      <p
                        style={{
                          margin: "0",
                          fontSize: "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.type}
                      </p>
                    </div>
                  </Card>
                </Badge>
              </Col>
            ))}
          </Row>
          {/* Pagination for desktop */}
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={total}
              showSizeChanger={true}
              onChange={handleDesktopPagination}
              pageSizeOptions={["5", "10", "20", "50"]}
            />
          </div>
        </>
      )}
    </div>
  );
};
